import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";
import { articlesByLang, type Article, supportedLangs, defaultLang } from "@/lib/articles";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import { articleImages } from "@/mocks/articleImages";
import { resolveLang } from "@/i18n/lang";
import { articleSchema, faqSchema } from "@/lib/seo";

function ArticleContent({
  article,
  lang,
  image,
}: {
  article: Article;
  lang: string;
  image: string;
}) {
  const { t } = useTranslation();

  return (
    <article className="mx-auto max-w-3xl">
      {/* Breadcrumb */}
      <nav className="text-sm text-foreground-500 mb-6 flex flex-wrap items-center gap-1">
        <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors">
          {t("nav.home")}
        </Link>
        <span className="text-foreground-300">/</span>
        <span className="text-foreground-600">{t("nav.articles")}</span>
      </nav>

      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-foreground-900 leading-tight mb-4">
        {article.h1}
      </h1>

      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-6 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <i className="ri-time-line"></i>
          {article.readingTime}
        </span>
        <span className="inline-flex items-center gap-1">
          <i className="ri-calendar-line"></i>
          {article.updatedDate}
        </span>
      </div>

      <img
        src={image}
        alt={article.h1}
        title={`${article.h1} — usfans spreadsheet`}
        className="w-full h-56 md:h-80 object-cover object-top rounded-lg mb-8"
      />

      <p className="text-lg text-foreground-700 leading-relaxed mb-8">{article.intro}</p>

      {article.sections.map((section, i) => (
        <section key={i} className="mb-8">
          {section.heading && (
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-4">
              {section.heading}
            </h2>
          )}
          {section.paragraphs?.map((p, j) => (
            <p key={j} className="text-foreground-700 leading-relaxed mb-4">
              {p}
            </p>
          ))}
          {section.list && (
            <ul className="space-y-3 mb-4">
              {section.list.map((item, j) => (
                <li key={j} className="flex items-start gap-3 text-foreground-700 leading-relaxed">
                  <i className="ri-check-line text-accent-600 mt-1"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
          {section.table && (
            <div className="overflow-x-auto rounded-lg border border-background-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {section.table.headers.map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-semibold text-foreground-900 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.table.rows.map((row, r) => (
                    <tr key={r} className="border-t border-background-200">
                      {row.map((cell, c) => (
                        <td
                          key={c}
                          className={`px-4 py-3 ${
                            c === 0 ? "font-medium text-foreground-800" : "text-foreground-600"
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      {/* FAQ */}
      <section className="mb-10">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-900 mb-5">FAQ</h2>
        <div className="space-y-3">
          {article.faq.map((item) => (
            <details key={item.q} className="rounded-lg border border-background-200 bg-background-50 group">
              <summary className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer text-foreground-900 font-medium">
                {item.q}
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="px-5 pb-4 text-foreground-600 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Related categories (internal links) */}
      <section className="mb-10 rounded-lg bg-background-100 p-6">
        <h2 className="font-heading font-bold text-lg text-foreground-900 mb-4">{t("article.related")}</h2>
        <div className="flex flex-wrap gap-2">
          {article.relatedCategories.map((catId) => {
            const cat = categories.find((c) => c.id === catId);
            if (!cat) return null;
            return (
              <a
                key={catId}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center rounded-full bg-background-50 border border-background-200 text-foreground-700 px-4 py-2 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {t(cat.nameKey)}
              </a>
            );
          })}
        </div>
      </section>

      {/* Back to home internal link */}
      <div className="flex flex-col sm:flex-row items-center gap-3 mb-10">
        <Link
          to={`/${lang}`}
          className="inline-flex items-center gap-2 rounded-md border border-background-300 text-foreground-800 px-5 py-3 text-sm font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          {t("article.backHome")}
        </Link>
        <a
          href={MAIN_SITE_URL}
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-colors"
        >
          {t("hero.ctaSecondary")}
        </a>
      </div>
    </article>
  );
}

export default function ArticlePage() {
  const { lang, slug } = useParams();
  const resolvedLang = resolveLang(lang);
  const articles = articlesByLang[resolvedLang] ?? [];
  const articleIndex = articles.findIndex((a) => a.slug === slug);
  const article = articleIndex >= 0 ? articles[articleIndex] : undefined;
  const { i18n } = useTranslation();
  const image = articleImages[articleIndex % articleImages.length];

  useEffect(() => {
    if (i18n.language !== resolvedLang) {
      i18n.changeLanguage(resolvedLang);
    }
    document.documentElement.lang = resolvedLang;
  }, [resolvedLang, i18n]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Redirect if unsupported lang or missing article (must be after all hooks)
  const isValidLang = lang && supportedLangs.includes(lang);
  if (!isValidLang || !article) {
    return <Navigate to={`/${defaultLang}`} replace />;
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema(article, resolvedLang, image)} />
      <JsonLd data={faqSchema(article.faq)} />
      <Header lang={resolvedLang} />
      <main className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-16">
        <ArticleContent article={article} lang={resolvedLang} image={image} />
      </main>
      <Footer lang={resolvedLang} />
    </div>
  );
}